import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Palette de commandes (Ctrl+K) — port fidèle du prototype (`app.js` buildPalette).
 * Recherche transverse : commandes, périmètres, interventions, engins, sites,
 * agents. ↑↓ pour naviguer, Entrée pour valider, Échap pour fermer.
 */

export interface PaletteItem {
  ico: string;
  t: string;
  k?: string;
  run: () => void;
}

interface Props {
  open: boolean;
  items: PaletteItem[];
  onClose: () => void;
}

const CommandPalette: React.FC<Props> = ({ open, items, onClose }) => {
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // À l'ouverture : on vide la recherche et on donne le focus au champ.
  useEffect(() => {
    if (open) {
      setQ('');
      setIdx(0);
      // focus au prochain tick (le champ vient d'être monté)
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return ql ? items.filter((i) => i.t.toLowerCase().includes(ql)) : items.slice(0, 15);
  }, [q, items]);

  // Garde l'index dans les bornes quand la liste change.
  useEffect(() => {
    setIdx((cur) => (shown.length === 0 ? 0 : Math.min(cur, shown.length - 1)));
  }, [shown.length]);

  // Fait défiler l'élément courant dans la vue.
  useEffect(() => {
    const el = listRef.current?.querySelectorAll('.pitem')[idx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [idx]);

  if (!open) return null;

  const run = (n: number) => {
    const i = shown[n];
    if (!i) return;
    onClose();
    i.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (shown.length) setIdx((c) => (c + 1) % shown.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (shown.length) setIdx((c) => (c - 1 + shown.length) % shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(idx);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <>
      <div className="palette-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="palette" role="dialog" aria-modal="true" aria-label="Palette de commandes">
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Rechercher une intervention, un engin, une action…"
          autoComplete="off"
        />
        <div className="palette__list" ref={listRef}>
          {shown.length ? (
            shown.map((i, n) => (
              <div
                key={n}
                className={`pitem ${n === idx ? 'is-cur' : ''}`}
                onMouseEnter={() => setIdx(n)}
                onClick={() => run(n)}
              >
                <span className="pitem__ico">{i.ico}</span>
                <span className="pitem__t">{i.t}</span>
                {i.k ? <span className="pitem__k">{i.k}</span> : null}
              </div>
            ))
          ) : (
            <div className="empty" style={{ margin: 8 }}>Aucun résultat.</div>
          )}
        </div>
        <div className="palette__foot">
          <kbd>↑</kbd>
          <kbd>↓</kbd> naviguer · <kbd>Entrée</kbd> valider · <kbd>Échap</kbd> fermer
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
